import React from 'react';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';

import { Box, Button, Heading, Text, TextButton } from '@zillow/constellation-10';
import { LoadingConstants, SpinnerContainer } from '@zg-rentals/rental-manager-env';
import {
  AuthCheckWrapper,
  HeadWrapper,
  numberUtils,
  useIsScreenDesktop,
  useRentalManagerUser,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';
import type { AdvanvedTableGroupedRows, ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable, StyledTableColumn, StyledTableHeaderRow } from '@zg-rentals/advanced-table';
import {
  type AdproductsZuoraBillingInvoiceDto,
  type AdproductsZuoraInvoicePaymentDto,
  ZuoraInvoiceType,
} from '@zg-rentals/ts-adproducts-client';

// Constants
import BillingConstants from '../../constants/BillingConstants';

// Components
import BillingTopBanner from '../../components/BillingTopBanner';
import BillingError from '../../components/BillingError';
import BillingViewAll from '../../components/BillingViewAll';
import BillingEmptyBanner from '../../components/BillingEmptyBanner';
import BillingTableErrorRow from '../../components/billingTableErrorRow/BillingTableErrorRow';

import billingUtils from '../../utils/billingUtils';

import type { InvoiceListFilterConfig } from '../../hooks/useInvoiceList';
import { useInvoiceList } from '../../hooks/useInvoiceList';
import { useAppMaintenance } from '../../hooks/useMaintenance';
import { InvoicePaymentDisplay } from '../../components/InvoicePaymentDisplay';
import { InvoiceFilter } from './InvoiceFilter';
import { css } from '@/styled-system/css';

type BillingInvoicePageProps = {
  emptyMessage: {
    text: Array<string>;
    title: string;
  };
  fullColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>>;
  groupingHeaderRenderer?: (group: AdvanvedTableGroupedRows<AdproductsZuoraBillingInvoiceDto>) => JSX.Element;
  groupingFunction?: (invoice: AdproductsZuoraBillingInvoiceDto) => string;
  isPaymentDue: boolean;
  mobileColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>>;
  showHistory: boolean;
  title: string;
};

const formatDate = (value: string | number) => value && format(new Date(value), 'MM/dd/yyyy hh:mm aa');
const rowSelectionDisabled = (rowData: AdproductsZuoraBillingInvoiceDto) =>
  rowData.invoiceType === ZuoraInvoiceType.NUCLEUS_LISTING;

export const BillingInvoicePage = ({
  emptyMessage,
  fullColumnConfig,
  isPaymentDue,
  mobileColumnConfig,
  groupingHeaderRenderer,
  groupingFunction,
  title,
  showHistory,
}: BillingInvoicePageProps) => {
  const {
    invoices,
    hasMoreData,
    fetchBillingData,
    loadingState,
    errorMsg,
    fetchPropertyData,
    setFilters,
    filters,
    refreshInvoices,
  } = useInvoiceList({
    hasPayments: !isPaymentDue || false,
  });
  const isScreenDesktop = useIsScreenDesktop();
  const { user } = useRentalManagerUser();
  const paths = useRmwPaths();
  const paymentsEnabled = user.features.canAccessInvoicePayments;
  const { isMaintenanceMode } = useAppMaintenance();
  const isNucleusOnly = billingUtils.isOnlyNucleusUser(user);
  const tempColumnConfig = isScreenDesktop ? fullColumnConfig : mobileColumnConfig;
  const columnConfig = tempColumnConfig.slice(0);

  const filteredInvoices = invoices.slice(0);
  const [selected, setSelected] = React.useState(new Set<number>());
  const [isViewAllClicked, setViewAll] = React.useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);
  const history = useHistory();

  const data = isViewAllClicked ? filteredInvoices : filteredInvoices.slice(0, 8);

  const handlePayInvoiceClick = (invoiceId: string) => {
    history.push(`${paths.billingPage}/${paths.invoicePage}/${invoiceId}/payment`);
  };

  const handleEditClick = (accountNumber: string) => {
    history.push(`${paths.billingPage}/account/${accountNumber}`);
  };

  React.useEffect(() => {
    if (loadingState === LoadingConstants.SUCCESS) {
      setInitialLoadComplete(true);
    }
  }, [loadingState]);

  // The click action requires to live within a component
  if (isPaymentDue && paymentsEnabled) {
    columnConfig.push({
      width: 1,
      header: 'Auto Pay',
      renderer: (invoice) => {
        if (!invoice.canMakePayments) {
          return '-';
        }
        if (billingUtils.invoiceInAutopayRange(invoice)) {
          return (
            <TextButton onClick={() => handleEditClick(invoice.accountNumber)} disabled={isMaintenanceMode}>
              {invoice.accountAutopay ? 'ON' : 'OFF'}
            </TextButton>
          );
        }
        return '-';
      },
      align: 'center',
    });
    columnConfig.push({
      width: 1,
      header: '',
      renderer: (invoice) =>
        invoice.canMakePayments ? (
          <Button
            tone="brand"
            emphasis="filled"
            onClick={() => handlePayInvoiceClick(invoice.invoiceNumber)}
            disabled={isMaintenanceMode}
          >
            Pay
          </Button>
        ) : undefined,
      align: 'right',
    });
  }

  const handleSaveFilters = (newFilters: InvoiceListFilterConfig) => {
    setFilters(newFilters);
    setSelected(new Set());
  };

  const handleInfiniteScroll = (isVisible: boolean) => {
    if (isVisible && hasMoreData) {
      fetchBillingData();
    }
  };

  React.useEffect(() => {
    fetchBillingData();
    fetchPropertyData();
  }, []);

  const renderEmptyBanner = () => {
    if (filteredInvoices.length === 0 && loadingState === LoadingConstants.SUCCESS) {
      return <BillingEmptyBanner notification={emptyMessage} />;
    }
    return null;
  };

  const historyColumnConfig: Array<ColumnConfig<AdproductsZuoraInvoicePaymentDto>> = [
    {
      width: 1,
      header: '',
      renderer: () => ' ',
      align: 'right',
    },
    {
      width: 2,
      header: 'Date',
      renderer: (value: AdproductsZuoraInvoicePaymentDto) => formatDate(value.createdTime),
      align: 'left',
    },
    {
      width: 3,
      header: 'Amount',
      renderer: (value: AdproductsZuoraInvoicePaymentDto) => numberUtils.asCurrency(value.appliedPaymentAmount),
      align: 'center',
    },
    {
      width: 3,
      header: 'Payment Number',
      dataKey: 'paymentNumber',
      align: 'center',
    },
    {
      width: 3,
      header: 'Status',
      dataKey: 'status',
      align: 'center',
      renderer: (value) => <InvoicePaymentDisplay value={value} />,
    },
  ];

  if (paymentsEnabled) {
    historyColumnConfig.splice(1, 0, {
      width: 3,
      header: 'Payment Method',
      align: 'center',
      renderer: billingUtils.formatPaymentMethod,
    });
    historyColumnConfig.push({
      width: 3,
      header: 'Paid By',
      align: 'center',
      renderer: (value) => (value.email ? value.email : '-'),
    });
  }

  const renderExpandibleComponent = (rowData: AdproductsZuoraBillingInvoiceDto) => (
    <div>
      <StyledTableHeaderRow display="flex">
        <StyledTableColumn width={`${(1 / 12) * 100}%`} />
        <StyledTableColumn width={`${(11 / 12) * 100}%`}>
          <Heading level={6} css={{ paddingBottom: 'sm' }}>
            Payment History
          </Heading>
        </StyledTableColumn>
      </StyledTableHeaderRow>
      <AdvancedTable
        columnConfig={historyColumnConfig}
        data={rowData.invoicePayments.slice().sort((a, b) => b.createdTime - a.createdTime)}
        stickyHeader={false}
      />
    </div>
  );

  const renderContent = () => {
    return (
      <>
        {!isNucleusOnly && (
          <BillingTopBanner
            selected={selected}
            data={data}
            deselectAll={() => setSelected(new Set())}
            refreshInvoices={refreshInvoices}
          />
        )}
        <InvoiceFilter setFilters={handleSaveFilters} filters={filters} isPaymentDue={isPaymentDue} />
        <AdvancedTable
          columnConfig={columnConfig}
          data={data}
          selected={selected}
          onSelectRow={setSelected}
          selectionDisabled={isNucleusOnly}
          rowSelectionDisabled={rowSelectionDisabled}
          hasMoreData={isViewAllClicked && hasMoreData && loadingState !== LoadingConstants.ERROR}
          ErrorRowComponent={loadingState === LoadingConstants.ERROR ? BillingTableErrorRow : undefined}
          handleLoadMoreData={handleInfiniteScroll}
          isLoading={loadingState === LoadingConstants.LOADING || loadingState === LoadingConstants.INITIAL}
          isMobile={!isScreenDesktop}
          groupingFunction={groupingFunction}
          groupingHeaderRenderer={groupingHeaderRenderer}
          isRowExpandable={(rowData) => showHistory && rowData.invoicePayments && rowData.invoicePayments.length > 0}
          ExpandedComponent={renderExpandibleComponent}
        />
        {renderEmptyBanner()}
        {!isViewAllClicked && (filteredInvoices.length > 8 || hasMoreData) && (
          <BillingViewAll onClick={() => setViewAll(true)} />
        )}
      </>
    );
  };

  return (
    <AuthCheckWrapper>
      <div>
        <HeadWrapper title={title} />
        {errorMsg && <BillingError errorMsg={errorMsg} />}
        {loadingState === LoadingConstants.ERROR && <BillingError errorMsg={BillingConstants.INVOICE_LOAD_ERROR} />}
        {initialLoadComplete ? renderContent() : <SpinnerContainer />}
        {!isNucleusOnly && (
          <Box
            className={css({ padding: '16px !important', marginTop: '40px !important' })}
            css={{ display: 'flex', flexWrap: 'wrap' }}
          >
            <Box css={{ width: '100%' }}>
              {!paymentsEnabled && (
                <Text textStyle="body-xs-bold" css={{ color: 'gray.500' }}>
                  {BillingConstants.BILLING_INFO_FOOTNOTE}
                </Text>
              )}
            </Box>
          </Box>
        )}
      </div>
    </AuthCheckWrapper>
  );
};

export default BillingInvoicePage;
